<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">

  <div class="content-section">
    <h2>Assurance RC Pro Médicale : Une Protection Indispensable</h2>
    <p class="intro-text">
      L'assurance <strong>Responsabilité Civile Professionnelle Médicale</strong> est une couverture 
      spécialisée et obligatoire pour tous les professionnels de santé. Elle protège contre les 
      conséquences financières des erreurs médicales, diagnostics erronés, complications, ou défauts 
      d'information qui peuvent survenir dans l'exercice de la médecine et des soins.
    </p>

    <div class="obligation-alert">
      <div class="alert-header">
        <i class="fas fa-exclamation-triangle"></i>
        <h3>Obligation Légale Stricte</h3>
      </div>
      <p>
        Depuis la loi Kouchner de 2002, <strong>tous les professionnels de santé</strong> doivent 
        obligatoirement être assurés en responsabilité civile professionnelle. L'exercice sans 
        assurance est interdit et sanctionné par l'Ordre professionnel. L'attestation d'assurance 
        doit être fournie lors de l'inscription à l'Ordre et renouvelée chaque année.
      </p>
      <div class="sanctions">
        <strong>Sanctions :</strong> Interdiction d'exercer, radiation de l'Ordre, sanctions pénales
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Professionnels de Santé Concernés</h2>

    <div class="professions-medicales">
      <div class="profession-group">
        <div class="group-header">
          <i class="fas fa-user-md"></i>
          <h3>Médecins et Spécialistes</h3>
        </div>
        <div class="profession-grid">
          <div class="profession-item">
            <i class="fas fa-stethoscope"></i>
            <span>Médecins généralistes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-heartbeat"></i>
            <span>Cardiologues</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-baby"></i>
            <span>Gynécologues-obstétriciens</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-cut"></i>
            <span>Chirurgiens (toutes spécialités)</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-bone"></i>
            <span>Anesthésistes-réanimateurs</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-brain"></i>
            <span>Radiologues</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-eye"></i>
            <span>Ophtalmologues</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-tooth"></i>
            <span>ORL</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-child"></i>
            <span>Pédiatres</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-head-side-cough"></i>
            <span>Psychiatres</span>
          </div>
        </div>
      </div>

      <div class="profession-group">
        <div class="group-header">
          <i class="fas fa-tooth"></i>
          <h3>Chirurgiens-Dentistes</h3>
        </div>
        <div class="profession-grid">
          <div class="profession-item">
            <i class="fas fa-teeth"></i>
            <span>Omnipraticiens</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-teeth-open"></i>
            <span>Orthodontistes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-syringe"></i>
            <span>Parodontistes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-x-ray"></i>
            <span>Implantologues</span>
          </div>
        </div>
      </div>

      <div class="profession-group">
        <div class="group-header">
          <i class="fas fa-user-nurse"></i>
          <h3>Professions Paramédicales</h3>
        </div>
        <div class="profession-grid">
          <div class="profession-item">
            <i class="fas fa-notes-medical"></i>
            <span>Infirmiers libéraux (IDEL)</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-hand-holding-medical"></i>
            <span>Kinésithérapeutes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-hands"></i>
            <span>Ostéopathes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-spa"></i>
            <span>Pédicures-podologues</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-wheelchair"></i>
            <span>Ergothérapeutes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-comments"></i>
            <span>Orthophonistes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-baby-carriage"></i>
            <span>Sages-femmes</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-capsules"></i>
            <span>Pharmaciens</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-eye-dropper"></i>
            <span>Opticiens</span>
          </div>
          <div class="profession-item">
            <i class="fas fa-dna"></i>
            <span>Biologistes médicaux</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Garanties de la RC Pro Médicale</h2>

    <div class="garanties-medicales">
      <div class="garantie-principale">
        <div class="garantie-header">
          <i class="fas fa-shield-virus"></i>
          <h3>Garanties Principales (Incluses)</h3>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-user-injured"></i> Dommages Corporels aux Patients</h4>
          <p>
            Couverture des préjudices physiques causés aux patients : complications post-opératoires, 
            infections nosocomiales, erreurs de diagnostic, retard de diagnostic, effets secondaires 
            non anticipés, séquelles d'un traitement inadapté.
          </p>
          <div class="plafonds-medicaux">
            <div class="plafond-item">
              <span class="label">Médecine générale :</span>
              <span class="montant">3 000 000 € à 8 000 000 €</span>
            </div>
            <div class="plafond-item">
              <span class="label">Spécialités à risque moyen :</span>
              <span class="montant">5 000 000 € à 15 000 000 €</span>
            </div>
            <div class="plafond-item">
              <span class="label">Spécialités à haut risque :</span>
              <span class="montant">8 000 000 € à 30 000 000 €</span>
            </div>
          </div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-euro-sign"></i> Dommages Immatériels</h4>
          <p>
            Prise en charge des préjudices financiers : perte de revenus du patient, frais 
            supplémentaires engagés pour correction d'une erreur, manque à gagner, préjudice moral.
          </p>
          <div class="plafond-standard">Plafond habituel : 500 000 € à 3 000 000 €</div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-file-medical"></i> Défaut d'Information et Consentement</h4>
          <p>
            Couverture spécifique pour le non-respect de l'obligation d'information du patient sur 
            les risques, alternatives thérapeutiques, ou absence de consentement éclairé (très 
            fréquent dans les contentieux).
          </p>
          <div class="plafond-standard">Souvent intégrée dans le plafond principal</div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-gavel"></i> Défense Juridique et Recours</h4>
          <p>
            Prise en charge des frais d'avocat, d'expertise médicale contradictoire, de procédure 
            devant les tribunaux civils, pénaux, et disciplinaires (Ordre des médecins, CCI).
          </p>
          <div class="plafond-standard">Plafond : 50 000 € à 150 000 € par sinistre</div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-hospital"></i> Assistance et Gestion de Crise</h4>
          <p>
            Soutien psychologique, accompagnement en cas de médiatisation d'un événement grave, 
            aide à la gestion de la communication, assistance juridique immédiate 24h/24.
          </p>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-clock"></i> Garantie Subséquente (Post-Activité)</h4>
          <p>
            <strong>Essentielle :</strong> couvre les réclamations formulées après la cessation 
            d'activité (retraite, reconversion) pour des faits survenus pendant la période d'activité. 
            Les réclamations peuvent survenir des années après les soins.
          </p>
          <div class="duree-subsequente">Durée recommandée : 5 à 10 ans après cessation</div>
        </div>
      </div>

      <div class="garantie-optionnelle">
        <div class="garantie-header">
          <i class="fas fa-plus-square"></i>
          <h3>Extensions Possibles</h3>
        </div>

        <div class="extension-medicale">
          <div class="extension-title">
            <i class="fas fa-users"></i>
            <h4>Extension Collaborateurs et Remplaçants</h4>
            <span class="cout">+ 100 € à 500 €/personne/an</span>
          </div>
          <p>
            Couverture des assistants, collaborateurs, internes, et remplaçants travaillant sous 
            votre responsabilité au sein de votre cabinet ou structure.
          </p>
        </div>

        <div class="extension-medicale">
          <div class="extension-title">
            <i class="fas fa-globe"></i>
            <h4>Extension Géographique Internationale</h4>
            <span class="cout">+ 200 € à 1 500 €/an</span>
          </div>
          <p>
            Couvre l'exercice hors de France (missions humanitaires, congrès, expertises à l'étranger). 
            Attention : USA/Canada nécessitent souvent un contrat spécifique.
          </p>
        </div>

        <div class="extension-medicale">
          <div class="extension-title">
            <i class="fas fa-syringe"></i>
            <h4>Garantie Essais Cliniques et Recherche</h4>
            <span class="cout">Sur devis</span>
          </div>
          <p>
            Couverture spécifique si vous participez à des essais cliniques, protocoles de recherche, 
            ou expérimentations médicales.
          </p>
        </div>

        <div class="extension-medicale">
          <div class="extension-title">
            <i class="fas fa-laptop-medical"></i>
            <h4>Télémédecine et Téléconsultation</h4>
            <span class="cout">+ 150 € à 800 €/an</span>
          </div>
          <p>
            Extension indispensable si vous pratiquez la téléconsultation : erreurs de diagnostic 
            à distance, problèmes techniques, défaut de transmission d'informations.
          </p>
        </div>

        <div class="extension-medicale">
          <div class="extension-title">
            <i class="fas fa-flask"></i>
            <h4>Responsabilité Produits (Dispositifs Médicaux)</h4>
            <span class="cout">+ 200 € à 1 000 €/an</span>
          </div>
          <p>
            Pour les praticiens qui fabriquent ou commercialisent des dispositifs médicaux, prothèses, 
            orthèses sur mesure, préparations pharmaceutiques.
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Tarifs de la RC Pro Médicale par Spécialité</h2>
    <p class="section-intro">
      Les cotisations varient considérablement selon la spécialité, le niveau de risque, le mode 
      d'exercice (libéral/hospitalier/mixte), et le nombre d'actes réalisés.
    </p>

    <div class="tarifs-specialites">
      <div class="specialite-card risque-faible">
        <div class="specialite-header">
          <i class="fas fa-check-circle"></i>
          <h3>Spécialités à Risque Faible</h3>
        </div>
        <div class="tarif-range">
          <div class="tarif-montant">500 € - 3 000 €</div>
          <div class="tarif-periode">par an</div>
        </div>
        <ul class="specialite-list">
          <li><strong>Médecine générale :</strong> 800 € - 1 500 €</li>
          <li><strong>Homéopathie :</strong> 500 € - 1 000 €</li>
          <li><strong>Médecine du travail :</strong> 600 € - 1 200 €</li>
          <li><strong>Acupuncture :</strong> 500 € - 900 €</li>
          <li><strong>Médecine thermale :</strong> 500 € - 1 000 €</li>
          <li><strong>Biologie médicale :</strong> 800 € - 2 000 €</li>
          <li><strong>Pharmacien officine :</strong> 1 000 € - 2 500 €</li>
          <li><strong>Opticien :</strong> 600 € - 1 800 €</li>
        </ul>
      </div>

      <div class="specialite-card risque-moyen">
        <div class="specialite-header">
          <i class="fas fa-exclamation-circle"></i>
          <h3>Spécialités à Risque Moyen</h3>
        </div>
        <div class="tarif-range">
          <div class="tarif-montant">2 000 € - 8 000 €</div>
          <div class="tarif-periode">par an</div>
        </div>
        <ul class="specialite-list">
          <li><strong>Cardiologie :</strong> 3 000 € - 6 000 €</li>
          <li><strong>Gastro-entérologie :</strong> 2 500 € - 5 000 €</li>
          <li><strong>Pneumologie :</strong> 2 000 € - 4 500 €</li>
          <li><strong>Dermatologie :</strong> 2 500 € - 5 500 €</li>
          <li><strong>Pédiatrie :</strong> 2 500 € - 5 000 €</li>
          <li><strong>Psychiatrie :</strong> 2 000 € - 4 500 €</li>
          <li><strong>Radiologie :</strong> 3 000 € - 7 000 €</li>
          <li><strong>Médecine interne :</strong> 2 500 € - 5 500 €</li>
          <li><strong>Rhumatologie :</strong> 2 000 € - 4 500 €</li>
          <li><strong>Endocrinologie :</strong> 2 000 € - 4 500 €</li>
          <li><strong>Kinésithérapie :</strong> 300 € - 1 200 €</li>
          <li><strong>Ostéopathie :</strong> 400 € - 1 500 €</li>
          <li><strong>Infirmier libéral (IDEL) :</strong> 300 € - 1 000 €</li>
          <li><strong>Sage-femme :</strong> 1 500 € - 4 000 €</li>
        </ul>
      </div>

      <div class="specialite-card risque-eleve">
        <div class="specialite-header">
          <i class="fas fa-times-circle"></i>
          <h3>Spécialités à Risque Élevé</h3>
        </div>
        <div class="tarif-range">
          <div class="tarif-montant">5 000 € - 50 000 €+</div>
          <div class="tarif-periode">par an</div>
        </div>
        <ul class="specialite-list">
          <li><strong>Gynécologie-obstétrique :</strong> 15 000 € - 40 000 €</li>
          <li><strong>Chirurgie orthopédique :</strong> 10 000 € - 30 000 €</li>
          <li><strong>Chirurgie viscérale :</strong> 8 000 € - 25 000 €</li>
          <li><strong>Neurochirurgie :</strong> 20 000 € - 50 000 €+</li>
          <li><strong>Chirurgie cardiaque :</strong> 15 000 € - 45 000 €</li>
          <li><strong>Anesthésie-réanimation :</strong> 10 000 € - 35 000 €</li>
          <li><strong>Chirurgie plastique :</strong> 8 000 € - 25 000 €</li>
          <li><strong>ORL chirurgicale :</strong> 6 000 € - 18 000 €</li>
          <li><strong>Ophtalmologie chirurgicale :</strong> 6 000 € - 20 000 €</li>
          <li><strong>Urologie :</strong> 6 000 € - 18 000 €</li>
          <li><strong>Stomatologie :</strong> 5 000 € - 15 000 €</li>
        </ul>
      </div>

      <div class="specialite-card risque-dentaire">
        <div class="specialite-header">
          <i class="fas fa-tooth"></i>
          <h3>Chirurgiens-Dentistes</h3>
        </div>
        <div class="tarif-range">
          <div class="tarif-montant">800 € - 5 000 €</div>
          <div class="tarif-periode">par an</div>
        </div>
        <ul class="specialite-list">
          <li><strong>Omnipratique :</strong> 800 € - 2 000 €</li>
          <li><strong>Orthodontie :</strong> 1 500 € - 3 500 €</li>
          <li><strong>Parodontie :</strong> 1 200 € - 3 000 €</li>
          <li><strong>Implantologie :</strong> 2 000 € - 5 000 €</li>
          <li><strong>Chirurgie orale :</strong> 1 500 € - 4 000 €</li>
          <li><strong>Endodontie :</strong> 1 000 € - 2 500 €</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Facteurs Influençant le Tarif</h2>

    <div class="facteurs-tarif">
      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-user-md"></i>
        </div>
        <h3>1. Spécialité et Risque</h3>
        <p>
          Le facteur principal : les spécialités chirurgicales et l'obstétrique sont les plus 
          coûteuses en raison du risque élevé de complications graves et de montants d'indemnisation 
          importants.
        </p>
        <div class="facteur-impact">Impact : x2 à x50 selon la spécialité</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-briefcase"></i>
        </div>
        <h3>2. Mode d'Exercice</h3>
        <p>
          <strong>Libéral exclusif :</strong> prime plus élevée car vous assumez seul la responsabilité.<br>
          <strong>Mixte (libéral + salarié) :</strong> réduction possible si activité libérale limitée.<br>
          <strong>Hospitalier public :</strong> couvert par l'hôpital (pas besoin de RC Pro personnelle).
        </p>
        <div class="facteur-impact">Impact : -30% à +50%</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-procedures"></i>
        </div>
        <h3>3. Volume d'Activité</h3>
        <p>
          Plus vous réalisez d'actes, plus le risque est important. Les assureurs demandent le nombre 
          d'actes annuels, de consultations, d'interventions chirurgicales, ou d'accouchements.
        </p>
        <div class="facteur-impact">Impact : +10% à +100%</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-file-medical-alt"></i>
        </div>
        <h3>4. Techniques Pratiquées</h3>
        <p>
          Certaines techniques sont plus risquées : chirurgie laparoscopique, laser, poses d'implants, 
          anesthésies lourdes, etc. Elles peuvent entraîner des majorations de prime.
        </p>
        <div class="facteur-impact">Impact : +10% à +40%</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-history"></i>
        </div>
        <h3>5. Antécédents de Sinistres</h3>
        <p>
          Un historique chargé (sinistres fréquents ou graves) entraîne une forte majoration, voire 
          un refus d'assurance. À l'inverse, l'absence de sinistre peut donner droit à un bonus.
        </p>
        <div class="facteur-impact">Impact : -15% à +150%</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-calendar-alt"></i>
        </div>
        <h3>6. Ancienneté d'Exercice</h3>
        <p>
          Les jeunes praticiens bénéficient parfois de tarifs préférentiels les premières années. 
          L'expérience réduit statistiquement le risque d'erreur.
        </p>
        <div class="facteur-impact">Impact : -20% à -40% jeunes praticiens</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-shield-alt"></i>
        </div>
        <h3>7. Plafonds de Garantie</h3>
        <p>
          Des plafonds plus élevés augmentent la prime. Choisissez des montants adaptés aux risques 
          de votre spécialité (minimum légal 8M€ pour les spécialités à risque élevé).
        </p>
        <div class="facteur-impact">Impact : +15% à +60%</div>
      </div>

      <div class="facteur-item">
        <div class="facteur-icon">
          <i class="fas fa-hand-holding-usd"></i>
        </div>
        <h3>8. Franchise</h3>
        <p>
          Une franchise plus élevée (montant restant à votre charge) permet de réduire la cotisation. 
          Franchises courantes : 0 €, 1 000 €, 2 500 €, 5 000 € selon les contrats.
        </p>
        <div class="facteur-impact">Impact : -5% à -15%</div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Exemples de Sinistres Médicaux</h2>

    <div class="sinistres-medicaux">
      <div class="sinistre-medical">
        <div class="sinistre-header">
          <i class="fas fa-user-md"></i>
          <h3>Médecin Généraliste</h3>
        </div>
        <div class="sinistre-details">
          <h4>Cas : Retard de diagnostic d'un cancer</h4>
          <p class="scenario">
            Un patient consulte pour des douleurs abdominales. Le médecin diagnostique un problème 
            digestif bénin sans prescrire d'examens complémentaires. 6 mois plus tard, un cancer 
            du pancréas est découvert à un stade avancé. Le patient décède 1 an après.
          </p>
          <div class="indemnisation">
            <h5>Indemnisation accordée par les tribunaux :</h5>
            <ul>
              <li><i class="fas fa-euro-sign"></i> Préjudice moral des proches : 80 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Perte de chance de survie : 150 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Frais de justice et expertise : 25 000 €</li>
            </ul>
            <div class="total-prise-charge">
              <strong>Total pris en charge par la RC Pro :</strong> 255 000 €
            </div>
          </div>
        </div>
      </div>

      <div class="sinistre-medical">
        <div class="sinistre-header">
          <i class="fas fa-baby"></i>
          <h3>Gynécologue-Obstétricien</h3>
        </div>
        <div class="sinistre-details">
          <h4>Cas : Complications lors d'un accouchement</h4>
          <p class="scenario">
            Lors d'un accouchement par voie basse, le médecin tarde à pratiquer une césarienne 
            d'urgence malgré des signes de souffrance fœtale. L'enfant naît avec de graves séquelles 
            neurologiques (infirmité motrice cérébrale).
          </p>
          <div class="indemnisation">
            <h5>Indemnisation accordée :</h5>
            <ul>
              <li><i class="fas fa-euro-sign"></i> Préjudice de l'enfant (handicap à vie) : 2 500 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Assistance tierce personne (rente viagère) : 1 200 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Préjudice moral parents : 150 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Frais médicaux et appareillage : 300 000 €</li>
            </ul>
            <div class="total-prise-charge">
              <strong>Total pris en charge :</strong> 4 150 000 €
            </div>
          </div>
        </div>
      </div>

      <div class="sinistre-medical">
        <div class="sinistre-header">
          <i class="fas fa-cut"></i>
          <h3>Chirurgien Orthopédiste</h3>
        </div>
        <div class="sinistre-details">
          <h4>Cas : Erreur lors d'une prothèse de hanche</h4>
          <p class="scenario">
            Lors de la pose d'une prothèse de hanche, le chirurgien endommage le nerf sciatique. 
            Le patient souffre d'une paralysie partielle de la jambe et de douleurs chroniques 
            invalidantes nécessitant plusieurs interventions correctrices.
          </p>
          <div class="indemnisation">
            <h5>Indemnisation accordée :</h5>
            <ul>
              <li><i class="fas fa-euro-sign"></i> Préjudice corporel (IPP 35%) : 450 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Frais chirurgicaux correctifs : 60 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Perte de revenus professionnels : 120 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Préjudice moral et souffrance : 80 000 €</li>
            </ul>
            <div class="total-prise-charge">
              <strong>Total pris en charge :</strong> 710 000 €
            </div>
          </div>
        </div>
      </div>

      <div class="sinistre-medical">
        <div class="sinistre-header">
          <i class="fas fa-syringe"></i>
          <h3>Anesthésiste</h3>
        </div>
        <div class="sinistre-details">
          <h4>Cas : Complication anesthésique</h4>
          <p class="scenario">
            Lors d'une intervention chirurgicale programmée, le patient fait un arrêt cardiaque 
            suite à une réaction allergique non détectée à l'anesthésique. Malgré la réanimation, 
            il conserve des séquelles neurologiques importantes.
          </p>
          <div class="indemnisation">
            <h5>Indemnisation accordée :</h5>
            <ul>
              <li><i class="fas fa-euro-sign"></i> Préjudice corporel (IPP 60%) : 800 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Assistance tierce personne : 400 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Perte de revenus : 350 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Préjudice moral famille : 100 000 €</li>
            </ul>
            <div class="total-prise-charge">
              <strong>Total pris en charge :</strong> 1 650 000 €
            </div>
          </div>
        </div>
      </div>

      <div class="sinistre-medical">
        <div class="sinistre-header">
          <i class="fas fa-tooth"></i>
          <h3>Chirurgien-Dentiste</h3>
        </div>
        <div class="sinistre-details">
          <h4>Cas : Lésion nerveuse lors d'une extraction</h4>
          <p class="scenario">
            Lors de l'extraction d'une dent de sagesse, le praticien lèse le nerf alvéolaire. 
            Le patient souffre d'une perte de sensibilité permanente de la lèvre et du menton, 
            ainsi que de troubles de l'élocution et de la mastication.
          </p>
          <div class="indemnisation">
            <h5>Indemnisation accordée :</h5>
            <ul>
              <li><i class="fas fa-euro-sign"></i> Préjudice corporel (IPP 15%) : 80 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Préjudice esthétique : 25 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Soins correctifs et rééducation : 15 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Préjudice moral : 20 000 €</li>
            </ul>
            <div class="total-prise-charge">
              <strong>Total pris en charge :</strong> 140 000 €
            </div>
          </div>
        </div>
      </div>

      <div class="sinistre-medical">
        <div class="sinistre-header">
          <i class="fas fa-capsules"></i>
          <h3>Pharmacien</h3>
        </div>
        <div class="sinistre-details">
          <h4>Cas : Erreur de délivrance médicamenteuse</h4>
          <p class="scenario">
            Le pharmacien délivre un médicament à dosage élevé au lieu du dosage pédiatrique 
            prescrit pour un enfant de 5 ans. L'enfant fait un surdosage grave nécessitant une 
            hospitalisation et conserve des séquelles rénales.
          </p>
          <div class="indemnisation">
            <h5>Indemnisation accordée :</h5>
            <ul>
              <li><i class="fas fa-euro-sign"></i> Préjudice corporel (IPP 20%) : 200 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Frais médicaux et suivi à vie : 150 000 €</li>
              <li><i class="fas fa-euro-sign"></i> Préjudice moral enfant et famille : 80 000 €</li>
            </ul>
            <div class="total-prise-charge">
              <strong>Total pris en charge :</strong> 430 000 €
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Comment Réduire le Coût de votre RC Pro Médicale ?</h2>

    <div class="reduction-conseils">
      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-balance-scale"></i>
        </div>
        <h3>1. Comparez les Assureurs Spécialisés</h3>
        <p>
          Faites jouer la concurrence entre assureurs spécialisés dans les professions médicales 
          (MACSF, SHAM, MMA, Allianz, AXA, Generali). Les tarifs peuvent varier de 30% à 50% 
          pour une même couverture.
        </p>
        <div class="conseil-economie">Économie : 20% à 40%</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-user-graduate"></i>
        </div>
        <h3>2. Profitez des Tarifs Jeunes Praticiens</h3>
        <p>
          Si vous débutez votre activité, bénéficiez des réductions accordées aux jeunes installés 
          (généralement les 3 à 5 premières années d'exercice libéral).
        </p>
        <div class="conseil-economie">Économie : 30% à 50% les premières années</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-users"></i>
        </div>
        <h3>3. Adhérez aux Contrats Collectifs</h3>
        <p>
          Passez par votre Ordre professionnel, syndicat, ou association de spécialité qui négocient 
          des contrats de groupe avec des tarifs préférentiels.
        </p>
        <div class="conseil-economie">Économie : 10% à 25%</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-sliders-h"></i>
        </div>
        <h3>4. Adaptez Plafonds et Franchise</h3>
        <p>
          Choisissez des plafonds adaptés à votre risque réel sans surdimensionner. Une franchise 
          raisonnable (2 500 € à 5 000 €) peut réduire la prime de 10% à 15%.
        </p>
        <div class="conseil-economie">Économie : 10% à 20%</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-clipboard-check"></i>
        </div>
        <h3>5. Mettez en Place une Démarche Qualité</h3>
        <p>
          Certification HAS, accréditation, protocoles formalisés, formations continues, EPP 
          (Évaluation des Pratiques Professionnelles) sont valorisés par les assureurs.
        </p>
        <div class="conseil-economie">Économie : 5% à 15%</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-file-medical"></i>
        </div>
        <h3>6. Soignez votre Dossier Médical</h3>
        <p>
          Une traçabilité parfaite, des dossiers complets et à jour, le respect scrupuleux du 
          consentement éclairé réduisent considérablement le risque contentieux.
        </p>
        <div class="conseil-economie">Impact : Diminution du risque de sinistre</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-handshake"></i>
        </div>
        <h3>7. Privilégiez la Médiation</h3>
        <p>
          En cas de litige, favorisez la médiation plutôt que la procédure judiciaire. Cela limite 
          les coûts, préserve votre réputation, et évite la hausse de prime.
        </p>
        <div class="conseil-economie">Impact : Évite majoration future</div>
      </div>

      <div class="conseil-card">
        <div class="conseil-icon">
          <i class="fas fa-graduation-cap"></i>
        </div>
        <h3>8. Formation Continue et Veille</h3>
        <p>
          Participation régulière aux congrès, formations DPC, abonnement aux revues médicales : 
          restez à jour des bonnes pratiques et des recommandations pour limiter les erreurs.
        </p>
        <div class="conseil-economie">Impact : Prévention des sinistres</div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Questions Fréquentes (FAQ)</h2>

    <div class="faq-medicale">
      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Que se passe-t-il si j'exerce sans RC Pro Médicale ?</h3>
        <p>
          Vous vous exposez à des sanctions disciplinaires de l'Ordre (avertissement, blâme, suspension, 
          radiation), des sanctions pénales, et surtout vous devrez personnellement indemniser les 
          victimes sur votre patrimoine personnel. C'est un risque financier catastrophique.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Mon assurance couvre-t-elle mes remplaçants ?</h3>
        <p>
          Non, chaque médecin doit avoir sa propre RC Pro. Le remplaçant doit souscrire une assurance 
          spécifique "médecin remplaçant". Vous pouvez néanmoins souscrire une extension pour couvrir 
          vos collaborateurs permanents.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Suis-je couvert si j'exerce en établissement public ?</h3>
        <p>
          Si vous êtes salarié exclusif du service public hospitalier, l'établissement assume votre 
          responsabilité (pas besoin de RC Pro personnelle). Si vous avez une activité libérale en 
          parallèle, vous devez souscrire une RC Pro pour cette activité.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Combien de temps après mon activité puis-je être poursuivi ?</h3>
        <p>
          Le délai de prescription pour une faute médicale est de <strong>10 ans</strong> à compter 
          de la consolidation du dommage (et non de la faute). D'où l'importance de la garantie 
          subséquente après cessation d'activité.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> La télémédecine est-elle automatiquement couverte ?</h3>
        <p>
          Pas toujours. Vérifiez votre contrat : de nombreux assureurs exigent maintenant une extension 
          spécifique pour la téléconsultation, car les risques sont différents (diagnostic à distance, 
          problèmes techniques, défaut de transmission).
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Puis-je être radié si mon assureur résilie mon contrat ?</h3>
        <p>
          Oui, l'absence d'assurance RC Pro entraîne l'interdiction d'exercer. Si votre assureur 
          résilie (suite à sinistres répétés), vous devez trouver rapidement un nouvel assureur. 
          Il existe un "Bureau Central de Tarification" pour les praticiens en difficulté.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Mon assurance couvre-t-elle les missions humanitaires ?</h3>
        <p>
          Cela dépend de votre contrat. Vérifiez l'extension géographique et déclarez vos missions 
          à l'avance. Certains assureurs excluent les zones de conflit ou les interventions hors UE 
          sans extension spécifique.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Que faire en cas de sinistre ou de réclamation ?</h3>
        <p>
          1) Ne jamais reconnaître de faute ni négocier directement avec la victime.<br>
          2) Déclarer immédiatement le sinistre à votre assureur (dans les 5 jours).<br>
          3) Conserver tous les documents, dossiers médicaux, correspondances.<br>
          4) Coopérer pleinement avec votre assureur et son avocat.
        </p>
      </div>
    </div>
  </div>

  <div class="cta-section">
    <h2>Trouvez la Meilleure RC Pro Médicale</h2>
    <p>
      Protégez votre exercice médical et votre patrimoine avec une assurance adaptée à votre spécialité. 
      Comparez les offres des meilleurs assureurs médicaux et obtenez votre devis personnalisé.
    </p>
    <a href="/comparaison" class="cta-button">
      <i class="fas fa-user-md"></i> Comparer les RC Pro Médicales
    </a>
  </div>

</app-page-layout>
